import { Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDebouncedCallback } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import React from 'react';

import { useI18n } from '../../../../contexts/I18nProvider';
import EndOfMonthService from '../../../../services/EndOfMonthService';
import type { UpdateEmployeeEndOfMonthCommentQuery } from '../../../../types/api/payload/endMonth';
import handleErrorMessage from '../../../../utils/handleErrorMessage';

type Props = {
  companyId: string;
  comment: string | null;
  endOfMonthId: string;
  employeeId: string;
  refetchAllData: () => void;
};

type FormValues = {
  comment: string | null;
};

export default function EndOfMonthCommentTextInput({
  companyId,
  comment,
  endOfMonthId,
  employeeId,
  refetchAllData,
}: Props) {
  const { t } = useI18n();
  const form = useForm<FormValues>({
    initialValues: { comment: comment || '' },
    onValuesChange: (values) => {
      handleAutoSave(values.comment);
    },
  });

  const { mutate: updateEmployeeComment } = useMutation({
    mutationFn: (variables: UpdateEmployeeEndOfMonthCommentQuery) =>
      EndOfMonthService.updateEmployeeComment(companyId, variables),
    onSuccess: () => {
      refetchAllData();
    },
    onError: (error: any) => {
      showNotification({
        id: 'mark-user-notification-as-viewed-error',
        title: t('w.error'),
        message: handleErrorMessage(error, t),
        color: 'red',
        icon: <IconX />,
      });
    },
  });

  const handleAutoSave = useDebouncedCallback(
    async (comment: string | null) => {
      updateEmployeeComment({
        endOfMonthId: endOfMonthId,
        employeeId: employeeId,
        comment,
      });
    },
    1000
  );

  return (
    <Textarea
      size={'xs'}
      autosize
      minRows={1}
      maxRows={6}
      {...form.getInputProps('comment')}
    />
  );
}
